/**
 * Zod schemas for the `surveys` collection (ADR-054, ADR-067).
 *
 * Replaces the former `feedback` collection. Covers post-session ratings,
 * mid-session polls, and event-scoped questionnaires. Always anonymous in
 * v1 — organizers read aggregates only via the `getSurveyResults` callable.
 *
 * Multi-question shape: a survey holds an ordered `questions[]` array.
 * A 1-question survey is the degenerate case of the same shape, so the
 * admin and mobile UIs render single- and multi-question surveys with the
 * same code path. Presentation metadata lives per-question because each
 * question renders its own chart in the results view.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

export const surveyTypeSchema = z.enum(['single_choice', 'free_text', 'rating']);
export const surveyStatusSchema = z.enum(['draft', 'scheduled', 'open', 'closed']);

export const surveyQuestionSchema = z.object({
  /**
   * Stable identifier for the question within its survey. Generated at
   * create time. Used by `SurveyResponse.answers[]` to join answers back
   * to questions.
   */
  id: z.string().min(1),
  /** The prompt shown to attendees for this question. */
  prompt: z.string(),
  type: surveyTypeSchema,
  /** Choices for single_choice questions. Empty array for other types. */
  options: z.array(z.string()),

  // ── Presentation metadata (Power BI dashboard labels) ──────────────
  ratingAxisLabel: z.string().nullable(),
  ratingScaleMin: z.string().nullable(),
  ratingScaleMax: z.string().nullable(),
  optionAxisLabel: z.string().nullable(),
  responseLabel: z.string().nullable(),
  chartSubtitle: z.string().nullable(),
});

export const surveyDocumentSchema = z.object({
  eventId: z.string(),
  /** Null for event-scoped surveys. Set for session-scoped surveys. */
  sessionId: z.string().nullable(),
  createdByUserId: z.string(),

  /** Ordered list of questions. At least one. */
  questions: z.array(surveyQuestionSchema).min(1),

  /**
   * Per-survey anonymity flag. Always true in v1 — schema supports false
   * for future attributed surveys.
   */
  anonymous: z.boolean(),

  status: surveyStatusSchema,
  openAt: z.custom<TimestampLike>().nullable(),
  closeAt: z.custom<TimestampLike>().nullable(),

  /** The announcement notification, if one was created. */
  notificationId: z.string().nullable(),
});
